/**
 * @file 時間のつかいかた (カレンダー由来の計画の読み取り専用カード)。
 *
 * @what: 週にならした合計と種類の内訳、まなびの領域ごとの時間 (色つきバー)、いまのステップで
 *   計画にまだ出てこない領域、対応表にない予定、取り込み元の情報を出す。
 * @why: カレンダー = 時間配分の計画、進捗ログ・メモ = 実績、という分担 (代表 2026-08-20)。
 *   ここは計画をながめる面で、できたかどうかの照合はしない。バーは最大値を 100% とした
 *   相対幅で、分のテキストラベルを常に併記する (色・長さだけで読ませない)。
 * @failure: 描画のみ。データが無いケース (未同期) は親が呼び出し自体を出し分ける。
 */

import type { CSSProperties } from "react";
import { formatSourceUpdated } from "../api/exploration";
import { formatMinutes, type StudySchedule } from "../api/schedule";
import type { AttainmentModel } from "../model/types";
import { aggregateSchedule, uncoveredStageDomains } from "../model/scheduleAggregate";
import { scheduleMap } from "../model/scheduleMap";
import { domainColor } from "./domainColors";
import { DOMAIN_ICONS } from "./studyIcons";
import "./study.css";

export function TimePlanCard({
  schedule,
  model,
  currentStageId,
}: {
  schedule: StudySchedule;
  model: AttainmentModel;
  /** お子さんの「いまここ」のステップ。未設定なら領域の抜けの提示は出さない。 */
  currentStageId: string | null;
}) {
  const agg = aggregateSchedule(schedule, scheduleMap);
  const uncovered = currentStageId
    ? uncoveredStageDomains(model, currentStageId, agg.plannedDomainIds)
    : [];
  const maxDomainMinutes = agg.byDomain[0]?.minutes ?? 0;
  const domainLabel = (id: string) => model.domains.find((d) => d.id === id)?.label ?? id;

  return (
    <section className="study-timeplan-card">
      <p className="study-timeplan-total">
        週にならすと <strong>{formatMinutes(agg.weeklyTotalMinutes)}</strong>
        {agg.byKind.length > 0 ? (
          <span className="study-timeplan-kinds">
            {agg.byKind.map(({ kind, minutes }) => (
              <span key={kind.id} className="study-timeplan-kind-chip">
                {kind.label} {formatMinutes(minutes)}
              </span>
            ))}
          </span>
        ) : null}
      </p>

      {agg.byDomain.length > 0 ? (
        <div>
          <h3 className="study-card-title">まなびの領域ごとの時間（週あたり）</h3>
          <ul className="study-timeplan-bars" data-testid="timeplan-bars">
            {agg.byDomain.map(({ domainId, minutes }) => {
              const color = domainColor(domainId);
              return (
                <li
                  key={domainId}
                  className="study-timeplan-bar-row"
                  style={color ? ({ "--study-domain-color": color } as CSSProperties) : undefined}
                >
                  <span className="study-timeplan-bar-label">
                    {DOMAIN_ICONS[domainId] ? (
                      <span aria-hidden="true">{DOMAIN_ICONS[domainId]} </span>
                    ) : null}
                    {domainLabel(domainId)}
                  </span>
                  <span className="study-timeplan-bar-track">
                    <span
                      className="study-timeplan-bar"
                      style={{
                        width: `${maxDomainMinutes > 0 ? Math.max(4, Math.round((minutes / maxDomainMinutes) * 100)) : 0}%`,
                      }}
                    />
                  </span>
                  <span className="study-timeplan-bar-minutes">{formatMinutes(minutes)}</span>
                </li>
              );
            })}
          </ul>
          <p className="study-card-help">
            ひとつの予定が複数の領域にまたがるときは、時間を等分して数えたおおよその目安です。
          </p>
        </div>
      ) : (
        <p className="study-muted">領域に割り当てられた予定がまだありません。</p>
      )}

      {uncovered.length > 0 ? (
        <div>
          <h3 className="study-card-title">計画にまだ出てこない領域</h3>
          <p className="study-timeplan-uncovered" data-testid="timeplan-uncovered">
            {uncovered.map((d) => (
              <span key={d.id} className="study-timeplan-uncovered-chip">
                {DOMAIN_ICONS[d.id] ? <span aria-hidden="true">{DOMAIN_ICONS[d.id]} </span> : null}
                {d.label}
              </span>
            ))}
          </p>
          <p className="study-card-help">
            いまのステップに単元がある領域のうち、カレンダーの予定に出てこないものです。園や
            日々のあそびで足りていることも多いので、ながめる目安としてどうぞ。
          </p>
        </div>
      ) : null}

      {agg.unmapped.length > 0 ? (
        <div>
          <h3 className="study-card-title">まだ分類していない予定</h3>
          <ul className="study-timeplan-unmapped" data-testid="timeplan-unmapped">
            {agg.unmapped.map(({ label, minutes }) => (
              <li key={label}>
                <span>{label}</span>
                <span className="study-muted">{formatMinutes(minutes)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="study-timeplan-footer study-muted">
        {formatSourceUpdated(schedule.window_start)}からの{agg.windowDays}日分の計画（カレンダー「
        {schedule.calendar_name.split("/").pop()}」）
      </p>
    </section>
  );
}
